import { Link } from "react-router-dom";

export const STATUS_LABEL = {
  pending_review: "Waiting for admin", changes_requested: "Changes requested", sent: "Sent", accepted: "Accepted",
  converted: "Converted · project live", completed: "Signed & returned", expired: "Expired",
};
export const STATUS_TONE = {
  pending_review: "bg-amber-100 text-amber-900 border-amber-300", changes_requested: "bg-orange-100 text-orange-900 border-orange-300",
  sent: "bg-blue-100 text-blue-800 border-blue-200", accepted: "bg-teal-100 text-teal-800 border-teal-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200", completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-slate-200 text-slate-700 border-slate-300",
};
export const REVIEWABLE = ["pending_review", "changes_requested"];
export const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function DraftStatusPill({ doc }) {
  const key = doc.is_expired && doc.status === "sent" ? "expired" : doc.status;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_TONE[key] || "bg-slate-100 text-slate-600 border-slate-200"}`}
          data-testid={`sales-status-${doc.id}`}>{STATUS_LABEL[key] || key}</span>
  );
}

export function PageHeader({ eyebrow, title, blurb, action }) {
  return (
    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
      <div>
        <div className="tinylabel text-emerald-700">{eyebrow}</div>
        <h1 className="text-3xl font-black">{title}</h1>
        {blurb && <p className="text-slate-500 mt-1 text-sm max-w-2xl">{blurb}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = "slate", testId }) {
  const tones = { slate: "border-slate-200", emerald: "border-emerald-300 bg-emerald-50/40", amber: "border-amber-300 bg-amber-50/40" };
  return (
    <div className={`bg-white border rounded-md p-4 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="tinylabel">{label}</div>
      <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// Banner on a rep's draft: what's happening and what (if anything) they need to do.
export function DraftBanner({ doc, kind, onEdit }) {
  const who = kind === "proposal" ? "the client" : "the worker";
  if (doc.status === "pending_review") return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="draft-banner-pending">
      <b>Waiting for admin review.</b> Submitted {shortDate(doc.submitted_at)}. Nothing has gone to {who} yet — you'll get an email once it's approved or sent back.
    </div>
  );
  if (doc.status === "changes_requested") return (
    <div className="rounded-md border-2 border-orange-400 bg-orange-50 p-4 text-sm text-orange-950" data-testid="draft-banner-changes">
      <div className="font-black text-base">Changes requested{doc.reviewed_at ? ` · ${shortDate(doc.reviewed_at)}` : ""}</div>
      <p className="mt-1 italic">“{doc.review_note || "No note left."}”</p>
      {onEdit && <button onClick={onEdit} data-testid="draft-banner-edit" className="mt-3 h-10 px-4 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-md text-sm">Make the changes &amp; resubmit</button>}
    </div>
  );
  if (doc.status === "sent") return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="draft-banner-sent">
      {doc.reviewed_by ? <><b>Approved &amp; sent</b> to {who} on {shortDate(doc.sent_at)} by {doc.reviewed_by}.</> : <><b>Sent</b> to {who} on {shortDate(doc.sent_at)} — no approval needed; the office was notified.</>}{doc.review_note ? <> Admin note: <i>“{doc.review_note}”</i></> : null}
      {kind === "proposal" && doc.expires_at ? <> Link expires {shortDate(doc.expires_at)}.</> : null}
    </div>
  );
  if (["accepted", "converted", "completed"].includes(doc.status)) return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="draft-banner-done">
      <b>{STATUS_LABEL[doc.status]}.</b> {kind === "proposal"
        ? (doc.status === "converted" ? <>The project is live — approved hours on it count toward your pay. <Link to="/sales" className="underline font-semibold">See it on your overview</Link>.</> : "The client accepted; an admin will set up the project.")
        : "The worker signed and returned their paperwork."}
    </div>
  );
  return null;
}
